import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Grid, Card, CardContent, Chip, Avatar, Stack
} from '@mui/material';
import {
  Search, TrendingUp, Handshake, SmartToy, Speed, Security,
  ArrowForward, Inventory2, Groups, AutoGraph, Bolt,
  ShoppingCart, LocalShipping, Star, VerifiedUser, PlayArrow,
  CheckCircle, Payment, Analytics
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08 } },
};

const features = [
  { icon: <Search />, title: 'Smart AI Search', desc: 'Describe what you need in plain English. Our RAG engine finds it instantly — no model numbers required.', gradient: 'linear-gradient(135deg, #4f46e5, #818cf8)' },
  { icon: <SmartToy />, title: 'Buyer Matching', desc: 'AI agents analyze search patterns and automatically connect sellers with the most relevant buyers.', gradient: 'linear-gradient(135deg, #7c3aed, #a78bfa)' },
  { icon: <Handshake />, title: 'AI Negotiation', desc: 'Get AI-powered fair price suggestions based on real market data, condition analysis, and demand signals.', gradient: 'linear-gradient(135deg, #059669, #34d399)' },
  { icon: <TrendingUp />, title: 'Demand Forecast', desc: 'Know what sells before listing. Our prediction engine shows demand trends and optimal pricing windows.', gradient: 'linear-gradient(135deg, #d97706, #fbbf24)' },
  { icon: <Payment />, title: 'Secure Payments', desc: 'Razorpay-powered escrow. Buyer pays platform, seller gets money after delivery confirmation.', gradient: 'linear-gradient(135deg, #dc2626, #f87171)' },
  { icon: <LocalShipping />, title: 'Integrated Logistics', desc: 'Ship via Delhivery, Blue Dart, or Shiprocket. Auto-generated labels with real-time tracking.', gradient: 'linear-gradient(135deg, #0891b2, #22d3ee)' },
];

const stats = [
  { value: '2,500+', label: 'Listed Items', icon: <Inventory2 /> },
  { value: '350+', label: 'Verified Sellers', icon: <Groups /> },
  { value: '₹2.4Cr', label: 'Traded Value', icon: <AutoGraph /> },
  { value: '4.8*', label: 'Seller Rating', icon: <Star /> },
];

const steps = [
  { num: '01', title: 'Upload Inventory', desc: 'List items individually or bulk-upload via CSV. AI auto-suggests pricing.', icon: <Inventory2 />, color: '#4f46e5' },
  { num: '02', title: 'Buyers Discover & Order', desc: 'Search, filter, add to cart, negotiate, or buy instantly.', icon: <ShoppingCart />, color: '#06b6d4' },
  { num: '03', title: 'Ship & Get Paid', desc: 'Accept, ship with tracking, buyer confirms — money in your account.', icon: <LocalShipping />, color: '#10b981' },
];

const testimonials = [
  { name: 'Rajesh Kumar', role: 'MD, Precision Motors Pvt Ltd', text: 'Recovered ₹18L from dead stock in 3 months. The AI pricing saved us from undervaluing our inventory.', avatar: 'R' },
  { name: 'Priya Sharma', role: 'Purchase Head, AutoFab Industries', text: 'Found OEM bearings at 40% below market price. The semantic search understands exactly what I need.', avatar: 'P' },
  { name: 'Suresh Patel', role: 'Operations, Gujarat Steel Works', text: 'Bulk upload feature saved us 2 weeks of manual listing. 800 items live in under an hour.', avatar: 'S' },
];

const LandingPage = () => {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* ═══════════ HERO ═══════════ */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(160deg, #0c0a1d 0%, #1a1145 35%, #0f172a 70%, #0c1222 100%)',
        pt: { xs: 8, md: 12 }, pb: { xs: 14, md: 20 },
        '&::before': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(79,70,229,0.15), transparent)',
        },
      }}>
        {/* Animated mesh gradient orbs */}
        <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 60%)', filter: 'blur(40px)' }} />
        <Box sx={{ position: 'absolute', bottom: '10%', left: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 60%)', filter: 'blur(40px)' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '40%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 60%)', filter: 'blur(30px)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={7}>
              <MotionBox initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                <Chip
                  icon={<Bolt sx={{ fontSize: 14, color: '#818cf8' }} />}
                  label="AI-Powered B2B Industrial Marketplace"
                  sx={{
                    mb: 3.5, py: 0.5, bgcolor: 'rgba(79,70,229,0.1)', color: '#c7d2fe',
                    fontWeight: 600, fontSize: '0.78rem', border: '1px solid rgba(79,70,229,0.25)',
                    backdropFilter: 'blur(10px)', borderRadius: 3,
                  }}
                />
                <Typography variant="h2" sx={{
                  color: 'white', fontWeight: 900, mb: 3,
                  fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' },
                  lineHeight: 1.05, letterSpacing: '-0.035em',
                }}>
                  Turn Idle Stock Into{' '}
                  <Box component="span" sx={{
                    background: 'linear-gradient(135deg, #818cf8 0%, #67e8f9 50%, #a78bfa 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    animation: 'gradient 4s ease infinite',
                    '@keyframes gradient': {
                      '0%': { backgroundPosition: '0% 50%' },
                      '50%': { backgroundPosition: '100% 50%' },
                      '100%': { backgroundPosition: '0% 50%' },
                    },
                  }}>
                    Revenue
                  </Box>
                </Typography>
                <Typography sx={{
                  color: '#94a3b8', mb: 5, fontWeight: 400, lineHeight: 1.8,
                  maxWidth: 520, fontSize: { xs: '1rem', md: '1.1rem' },
                }}>
                  India's smartest B2B marketplace for industrial surplus. 
                  6 AI agents handle pricing, matching, negotiation & logistics — 
                  so you focus on business, not busywork.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                  <Button variant="contained" size="large" component={Link} to="/register" endIcon={<ArrowForward />}>
                    Start Selling — It's Free
                  </Button>
                  <Button
                    variant="outlined" size="large" component={Link} to="/marketplace"
                    sx={{ borderColor: 'rgba(148,163,184,0.25)', color: '#e2e8f0', '&:hover': { borderColor: '#818cf8', bgcolor: 'rgba(79,70,229,0.08)', color: '#c7d2fe' } }}
                  >
                    Explore Marketplace
                  </Button>
                </Stack>

                {/* Trust row */}
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  {[
                    { icon: <VerifiedUser sx={{ fontSize: 15 }} />, text: 'GST Verified' },
                    { icon: <Payment sx={{ fontSize: 15 }} />, text: 'Razorpay Secured' },
                    { icon: <LocalShipping sx={{ fontSize: 15 }} />, text: 'Integrated Shipping' },
                    { icon: <Analytics sx={{ fontSize: 15 }} />, text: 'AI Analytics' },
                  ].map((b, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <Box sx={{ color: '#818cf8' }}>{b.icon}</Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>{b.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </MotionBox>
            </Grid>

            {/* Stats column */}
            <Grid item xs={12} md={5}>
              <MotionBox initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {stats.map((stat, i) => (
                    <MotionBox
                      key={i}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                    >
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2.5,
                        p: 2.5, borderRadius: 3,
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': { background: 'rgba(79,70,229,0.05)', borderColor: 'rgba(79,70,229,0.15)', transform: 'translateX(8px)' },
                      }}>
                        <Avatar sx={{ bgcolor: 'rgba(79,70,229,0.12)', color: '#818cf8', width: 50, height: 50 }}>
                          {stat.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>{stat.label}</Typography>
                        </Box>
                      </Box>
                    </MotionBox>
                  ))}
                </Box>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>

        {/* Bottom gradient fade */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to top, #fafbff, transparent)' }} />
      </Box>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Chip label="SIMPLE PROCESS" size="small" sx={{ mb: 2, fontWeight: 700, letterSpacing: '0.1em', bgcolor: 'rgba(79,70,229,0.06)' }} color="primary" />
            <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
              Three Steps to Sell
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              From listing to payment in the simplest flow possible
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, i) => (
              <Grid item xs={12} md={4} key={i}>
                <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
                  <Box sx={{ textAlign: 'center', p: 4, position: 'relative' }}>
                    <Box sx={{
                      width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 3,
                      background: `linear-gradient(135deg, ${step.color}12, ${step.color}08)`,
                      border: `2px solid ${step.color}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative', zIndex: 1,
                    }}>
                      <Box sx={{ color: step.color }}>{step.icon}</Box>
                    </Box>
                    <Typography sx={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%)', fontSize: '6rem', fontWeight: 900, color: 'rgba(79,70,229,0.03)', lineHeight: 1 }}>
                      {step.num}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>{step.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{step.desc}</Typography>
                  </Box>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════ FEATURES ═══════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: 'rgba(79,70,229,0.015)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Chip label="POWERED BY AI" size="small" sx={{ mb: 2, fontWeight: 700, letterSpacing: '0.1em', bgcolor: 'rgba(124,58,237,0.06)' }} color="secondary" />
            <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
              Intelligent Features
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
              Six AI agents work together to create India's most efficient B2B surplus marketplace
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: 2.5, mb: 2.5,
                      background: f.gradient,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', boxShadow: `0 8px 20px ${f.gradient.includes('#4f46e5') ? 'rgba(79,70,229,0.25)' : 'rgba(0,0,0,0.1)'}`,
                    }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>{f.desc}</Typography>
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════ TESTIMONIALS ═══════════ */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Chip label="TESTIMONIALS" size="small" sx={{ mb: 2, fontWeight: 700, letterSpacing: '0.1em', bgcolor: 'rgba(16,185,129,0.06)' }} color="success" />
            <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
              Trusted by Industry Leaders
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} md={4} key={i}>
                <MotionCard initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  sx={{ height: '100%', p: 0 }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                      {[...Array(5)].map((_, j) => <Star key={j} sx={{ fontSize: 16, color: '#f59e0b' }} />)}
                    </Box>
                    <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.8, color: '#475569', fontStyle: 'italic' }}>
                      "{t.text}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: '#4f46e5', fontSize: '0.9rem' }}>{t.avatar}</Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.role}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════ CTA ═══════════ */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 30%, #4f46e5 70%, #6366f1 100%)',
        py: { xs: 10, md: 12 }, textAlign: 'center',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}>
              Ready to Recover Value?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, maxWidth: 480, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
              Join 350+ companies turning surplus into revenue. No listing fees. Pay only when you sell.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" size="large" component={Link} to="/register" endIcon={<ArrowForward />}
                sx={{ bgcolor: 'white', color: '#4f46e5', px: 5, boxShadow: '0 10px 35px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#f8faff', transform: 'translateY(-3px)', boxShadow: '0 15px 45px rgba(0,0,0,0.25)' } }}>
                Create Free Account
              </Button>
              <Button variant="outlined" size="large" component={Link} to="/marketplace"
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white', px: 5, '&:hover': { borderColor: '#c7d2fe', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                Browse Listings
              </Button>
            </Stack>
          </MotionBox>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;

